"use strict";
import { UniverseChainId } from "features/chains/types";
import { contracts as autogenContracts } from "constants/contracts";
import {
  contractsConfig as clAutogenContracts
} from "constants/cl-contracts";
const PLACEHOLDER_ADDRESS = "0x0000000000000000000000000000000000000000";
export const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
const WBNB_ADDRESSES = {
  [UniverseChainId.Sepolia]: "0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd",
  [UniverseChainId.Mainnet]: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c"
};
function chainKey(chainId) {
  return chainId === UniverseChainId.Sepolia ? "bscTestnet" : "bscMainnet";
}
function autogenAddress(chainId, name) {
  return autogenContracts[chainKey(chainId)]?.[name]?.address ?? PLACEHOLDER_ADDRESS;
}
function clAutogenAddress(chainId, name) {
  return clAutogenContracts[chainKey(chainId)]?.[name]?.address ?? PLACEHOLDER_ADDRESS;
}
const PLACEHOLDER_SUBGRAPH_URL = "TODO";
function placeholderEndpoint(label) {
  return { url: PLACEHOLDER_SUBGRAPH_URL, label };
}
const BSC_TESTNET_ENDPOINTS = {
  v2: placeholderEndpoint("bsc-testnet-v2"),
  v3: placeholderEndpoint("bsc-testnet-v3"),
  gauges: placeholderEndpoint("bsc-testnet-gauges")
};
const BSC_MAINNET_ENDPOINTS = {
  v2: {
    url: "https://api.goldsky.com/api/public/project_cmgzljqwl006c5np2gnao4li4/subgraphs/topaz-v2/v0.0.4/gn",
    label: "bsc-mainnet-v2"
  },
  v3: {
    url: "https://api.goldsky.com/api/public/project_cmgzljqwl006c5np2gnao4li4/subgraphs/topaz-v3/v0.0.2/gn",
    label: "bsc-mainnet-v3"
  },
  gauges: placeholderEndpoint("bsc-mainnet-gauges")
};
function createEndpointAccessor(config) {
  const pick = (type) => {
    const endpoint = config[type];
    if (!endpoint) {
      throw new Error(`[bscContracts] No subgraph endpoint configured for "${type}"`);
    }
    if (endpoint.url === PLACEHOLDER_SUBGRAPH_URL) {
      throw new Error(
        `[bscContracts] Subgraph endpoint "${type}" is a TODO placeholder. Set it in packages/uniswap/src/constants/bscContracts.ts before using it.`
      );
    }
    return endpoint;
  };
  return {
    get v2() {
      return pick("v2");
    },
    get v3() {
      return pick("v3");
    },
    get gauges() {
      return pick("gauges");
    }
  };
}
export const BSC_SUBGRAPHS = {
  [UniverseChainId.Sepolia]: createEndpointAccessor(BSC_TESTNET_ENDPOINTS),
  [UniverseChainId.Mainnet]: createEndpointAccessor(BSC_MAINNET_ENDPOINTS)
};
function buildBscContracts(chainId) {
  return {
    // V2 AMM
    poolFactory: autogenAddress(chainId, "PoolFactory"),
    router: autogenAddress(chainId, "Router"),
    // V3 / Slipstream (from cl-contracts autogen)
    clFactory: clAutogenAddress(chainId, "CLFactory"),
    clPool: clAutogenAddress(chainId, "CLPool"),
    swapRouter: clAutogenAddress(chainId, "SwapRouter"),
    quoterV2: clAutogenAddress(chainId, "QuoterV2"),
    mixedRouteQuoter: clAutogenAddress(chainId, "MixedRouteQuoterV1"),
    nonfungiblePositionManager: clAutogenAddress(chainId, "NonfungiblePositionManager"),
    clGaugeFactory: clAutogenAddress(chainId, "CLGaugeFactory"),
    // Gauge / voting
    voter: autogenAddress(chainId, "Voter"),
    gaugeFactory: autogenAddress(chainId, "GaugeFactory"),
    rewardDistributor: autogenAddress(chainId, "RewardsDistributor"),
    feeDistributor: PLACEHOLDER_ADDRESS,
    // TODO(topaz): add to deployment manifest
    votingEscrow: autogenAddress(chainId, "VotingEscrow"),
    votingRewardsFactory: autogenAddress(chainId, "VotingRewardsFactory"),
    managedRewardsFactory: autogenAddress(chainId, "ManagedRewardsFactory"),
    // Periphery / governance
    forwarder: autogenAddress(chainId, "Forwarder"),
    veArtProxy: autogenAddress(chainId, "VeArtProxy"),
    minter: autogenAddress(chainId, "Minter"),
    factoryRegistry: autogenAddress(chainId, "FactoryRegistry"),
    airdropDistributor: autogenAddress(chainId, "AirdropDistributor"),
    epochGovernor: autogenAddress(chainId, "EpochGovernor"),
    protocolGovernor: autogenAddress(chainId, "ProtocolGovernor"),
    // Hardcoded canonical addresses
    wbnb: WBNB_ADDRESSES[chainId] ?? PLACEHOLDER_ADDRESS,
    topaz: autogenAddress(chainId, "Topaz")
  };
}
export const BSC_CONTRACTS = {
  [UniverseChainId.Sepolia]: buildBscContracts(UniverseChainId.Sepolia),
  [UniverseChainId.Mainnet]: buildBscContracts(UniverseChainId.Mainnet)
};
function buildClAddressMap(network) {
  const entries = clAutogenContracts[network] ?? {};
  const out = {};
  for (const [name, info] of Object.entries(entries)) {
    if (info?.address) {
      out[name] = info.address;
    }
  }
  return out;
}
function buildClAbiMap(network) {
  const entries = clAutogenContracts[network] ?? {};
  const out = {};
  for (const [name, info] of Object.entries(entries)) {
    if (info?.abi) {
      out[name] = info.abi;
    }
  }
  return out;
}
export const clContractAddresses = {
  bscTestnet: buildClAddressMap("bscTestnet"),
  bscMainnet: buildClAddressMap("bscMainnet")
};
export const clContractABIs = {
  bscTestnet: buildClAbiMap("bscTestnet"),
  bscMainnet: buildClAbiMap("bscMainnet")
};
export function getBscClAbi(chainId, name) {
  return clContractABIs[chainKey(chainId)][name];
}
export const SPECIAL_CASE_TOKENS = {};
export const BSC_FEE_TIERS = [100, 500, 3e3, 1e4];
export const TICK_SPACING_TO_FEE = {
  1: 100,
  50: 500,
  100: 1e3,
  200: 3e3,
  2e3: 1e4
};
export const FEE_TO_TICK_SPACING = {
  100: 1,
  500: 50,
  1e3: 100,
  3e3: 200,
  1e4: 2e3
};
export const BSC_V2_POOL_TYPES = [
  { stable: false, label: "volatile" },
  { stable: true, label: "stable" }
];
export function getBscContracts(chainId) {
  return BSC_CONTRACTS[chainId];
}
export function getBscSubgraphs(chainId) {
  return BSC_SUBGRAPHS[chainId];
}
export function isBscChain(chainId) {
  return chainId === UniverseChainId.Sepolia || chainId === UniverseChainId.Mainnet;
}
export function getDefaultBscChainId() {
  return UniverseChainId.Mainnet;
}
